/** Une police du catalogue : ses fichiers par style, découpés comme Google Fonts en alphabets latin et latin étendu. */
export interface TextFont {
  id: string;
  label: string;
  category: "sans" | "condensed" | "serif";
  files: Partial<Record<TextFontStyle, TextFontFile[]>>;
}

export type TextFontStyle = "regular" | "bold" | "italic";

export interface TextFontFile {
  url: string;
  unicodeRange: string[];
}

const LATIN = ["U+0000-00FF", "U+0131", "U+0152-0153", "U+02BB-02BC", "U+02C6", "U+02DA", "U+02DC", "U+0304", "U+0308", "U+0329", "U+2000-206F", "U+20AC", "U+2122", "U+2191", "U+2193", "U+2212", "U+2215", "U+FEFF", "U+FFFD"];
const LATIN_EXT = ["U+0100-02BA", "U+02BD-02C5", "U+02C7-02CC", "U+02CE-02D7", "U+02DD-02FF", "U+0304", "U+0308", "U+0329", "U+1D00-1DBF", "U+1E00-1E9F", "U+1EF2-1EFF", "U+2020", "U+20A0-20AB", "U+20AD-20C0", "U+2113", "U+2C60-2C7F", "U+A720-A7FF"];

function latinFiles(latin: URL, latinExt: URL): TextFontFile[] {
  return [{ url: latin.href, unicodeRange: LATIN }, { url: latinExt.href, unicodeRange: LATIN_EXT }];
}

/** Polices libres (licence OFL, fichiers Fontsource), livrées avec le moteur. Sans `ShapeText.font`, le texte reste en Noto Sans. */
export const TEXT_FONTS: TextFont[] = [
  {
    id: "inter",
    label: "Inter",
    category: "sans",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/inter/inter-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/inter/inter-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/inter/inter-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/inter/inter-latin-ext-700-normal.woff2", import.meta.url),
      ),
      italic: latinFiles(
        new URL("../../assets/fonts/inter/inter-latin-400-italic.woff2", import.meta.url),
        new URL("../../assets/fonts/inter/inter-latin-ext-400-italic.woff2", import.meta.url),
      ),
    },
  },
  {
    id: "roboto",
    label: "Roboto",
    category: "sans",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/roboto/roboto-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/roboto/roboto-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/roboto/roboto-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/roboto/roboto-latin-ext-700-normal.woff2", import.meta.url),
      ),
      italic: latinFiles(
        new URL("../../assets/fonts/roboto/roboto-latin-400-italic.woff2", import.meta.url),
        new URL("../../assets/fonts/roboto/roboto-latin-ext-400-italic.woff2", import.meta.url),
      ),
    },
  },
  {
    id: "open-sans",
    label: "Open Sans",
    category: "sans",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/open-sans/open-sans-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/open-sans/open-sans-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/open-sans/open-sans-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/open-sans/open-sans-latin-ext-700-normal.woff2", import.meta.url),
      ),
      italic: latinFiles(
        new URL("../../assets/fonts/open-sans/open-sans-latin-400-italic.woff2", import.meta.url),
        new URL("../../assets/fonts/open-sans/open-sans-latin-ext-400-italic.woff2", import.meta.url),
      ),
    },
  },
  {
    id: "lato",
    label: "Lato",
    category: "sans",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/lato/lato-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/lato/lato-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/lato/lato-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/lato/lato-latin-ext-700-normal.woff2", import.meta.url),
      ),
      italic: latinFiles(
        new URL("../../assets/fonts/lato/lato-latin-400-italic.woff2", import.meta.url),
        new URL("../../assets/fonts/lato/lato-latin-ext-400-italic.woff2", import.meta.url),
      ),
    },
  },
  {
    id: "montserrat",
    label: "Montserrat",
    category: "sans",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/montserrat/montserrat-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/montserrat/montserrat-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/montserrat/montserrat-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/montserrat/montserrat-latin-ext-700-normal.woff2", import.meta.url),
      ),
      italic: latinFiles(
        new URL("../../assets/fonts/montserrat/montserrat-latin-400-italic.woff2", import.meta.url),
        new URL("../../assets/fonts/montserrat/montserrat-latin-ext-400-italic.woff2", import.meta.url),
      ),
    },
  },
  {
    id: "poppins",
    label: "Poppins",
    category: "sans",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/poppins/poppins-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/poppins/poppins-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/poppins/poppins-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/poppins/poppins-latin-ext-700-normal.woff2", import.meta.url),
      ),
      italic: latinFiles(
        new URL("../../assets/fonts/poppins/poppins-latin-400-italic.woff2", import.meta.url),
        new URL("../../assets/fonts/poppins/poppins-latin-ext-400-italic.woff2", import.meta.url),
      ),
    },
  },
  {
    id: "roboto-condensed",
    label: "Roboto Condensed",
    category: "condensed",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/roboto-condensed/roboto-condensed-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/roboto-condensed/roboto-condensed-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/roboto-condensed/roboto-condensed-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/roboto-condensed/roboto-condensed-latin-ext-700-normal.woff2", import.meta.url),
      ),
      italic: latinFiles(
        new URL("../../assets/fonts/roboto-condensed/roboto-condensed-latin-400-italic.woff2", import.meta.url),
        new URL("../../assets/fonts/roboto-condensed/roboto-condensed-latin-ext-400-italic.woff2", import.meta.url),
      ),
    },
  },
  {
    id: "oswald",
    label: "Oswald",
    category: "condensed",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/oswald/oswald-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/oswald/oswald-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/oswald/oswald-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/oswald/oswald-latin-ext-700-normal.woff2", import.meta.url),
      ),
    },
  },
  {
    id: "merriweather",
    label: "Merriweather",
    category: "serif",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/merriweather/merriweather-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/merriweather/merriweather-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/merriweather/merriweather-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/merriweather/merriweather-latin-ext-700-normal.woff2", import.meta.url),
      ),
      italic: latinFiles(
        new URL("../../assets/fonts/merriweather/merriweather-latin-400-italic.woff2", import.meta.url),
        new URL("../../assets/fonts/merriweather/merriweather-latin-ext-400-italic.woff2", import.meta.url),
      ),
    },
  },
  {
    id: "playfair-display",
    label: "Playfair Display",
    category: "serif",
    files: {
      regular: latinFiles(
        new URL("../../assets/fonts/playfair-display/playfair-display-latin-400-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/playfair-display/playfair-display-latin-ext-400-normal.woff2", import.meta.url),
      ),
      bold: latinFiles(
        new URL("../../assets/fonts/playfair-display/playfair-display-latin-700-normal.woff2", import.meta.url),
        new URL("../../assets/fonts/playfair-display/playfair-display-latin-ext-700-normal.woff2", import.meta.url),
      ),
      italic: latinFiles(
        new URL("../../assets/fonts/playfair-display/playfair-display-latin-400-italic.woff2", import.meta.url),
        new URL("../../assets/fonts/playfair-display/playfair-display-latin-ext-400-italic.woff2", import.meta.url),
      ),
    },
  },
];

const FONTS_BY_ID = new Map(TEXT_FONTS.map((font) => [font.id, font]));

export function textFont(id: string | undefined): TextFont | undefined {
  return id === undefined ? undefined : FONTS_BY_ID.get(id);
}

/** Le style dessiné : celui demandé si la police l'a, sinon le normal (Oswald n'a pas d'italique). */
export function textFontStyle(font: TextFont, style: TextFontStyle): TextFontStyle {
  return font.files[style] ? style : "regular";
}

/** Nom de la police dans `text-font`, déclaré dans `font-faces`. */
export function textFontName(font: TextFont, style: TextFontStyle): string {
  const drawn = textFontStyle(font, style);
  return `${font.label} ${drawn === "regular" ? "Regular" : drawn === "bold" ? "Bold" : "Italic"}`;
}
